import React from 'react'
import { Edit2, FileText, Trash2 } from 'lucide-react'
import PersonAvatar from '../PersonAvatar'
import { formatPersonDisplayName } from '../../utils/people'
import type { CollectionItem } from '../../types/collections'

/**
 * Square avatar card for a Person. The view-only "header" (avatar + name +
 * optional relation pill + optional checkin count) is always rendered. The
 * action row at the bottom only appears if at least one of the action
 * callbacks is provided — used by the People collection page (full
 * functionality) but suppressed on the Collections list preview (view-only).
 */
interface PersonCardProps {
    person: CollectionItem
    /** Optional — rendered as "{N} checkins" beneath the relation pill.
     *  Used by the ranked rails to surface the rank metric inline. Omitted on
     *  the alphabetical grid since the count carries no signal there. */
    checkinCount?: number
    onView: () => void
    onEdit?: () => void
    onCheckins?: () => void
    onDelete?: () => void
}

const PersonCard: React.FC<PersonCardProps> = ({
    person,
    checkinCount,
    onView,
    onEdit,
    onCheckins,
    onDelete,
}) => {
    const data = person.data || {}
    const name = formatPersonDisplayName(data)
    const relation = trimOrEmpty(data.relation)
    const hasActions = onEdit || onCheckins || onDelete
    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 hover:border-sky-300 transition-colors">
            <button
                onClick={onView}
                className="w-full text-left p-3 focus:outline-none focus:ring-2 focus:ring-sky-500 rounded-t-lg flex flex-col items-center cursor-pointer"
            >
                <PersonAvatar
                    data={data}
                    className="h-16 w-16 mb-2"
                    initialsClassName="text-lg"
                    alt={name}
                />
                <div className="font-medium text-gray-900 text-center text-sm truncate w-full">
                    {name}
                </div>
                {relation && (
                    <div className="mt-1 inline-flex items-center px-2 py-0.5 rounded-full bg-sky-50 text-sky-700 text-xs">
                        {relation}
                    </div>
                )}
                {typeof checkinCount === 'number' && (
                    <div className="mt-1 text-xs text-gray-500">
                        {checkinCount} checkin{checkinCount === 1 ? '' : 's'}
                    </div>
                )}
            </button>
            {hasActions && (
                <div className="border-t border-gray-100 px-2 py-1 flex items-center justify-end gap-1">
                    {onCheckins && (
                        <button
                            onClick={onCheckins}
                            className="p-1 text-gray-600 hover:text-green-600 cursor-pointer"
                            title="View checkins with this person"
                        >
                            <FileText className="w-4 h-4" />
                        </button>
                    )}
                    {onEdit && (
                        <button
                            onClick={onEdit}
                            className="p-1 text-gray-600 hover:text-amber-900 cursor-pointer"
                            title="Edit person"
                        >
                            <Edit2 className="w-4 h-4" />
                        </button>
                    )}
                    {onDelete && (
                        <button
                            onClick={onDelete}
                            className="p-1 text-gray-600 hover:text-rose-600 cursor-pointer"
                            title="Delete person"
                        >
                            <Trash2 className="w-4 h-4" />
                        </button>
                    )}
                </div>
            )}
        </div>
    )
}

function trimOrEmpty(value: unknown): string {
    if (typeof value !== 'string') return ''
    return value.trim()
}

export default PersonCard
